import { useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";

/**
 * Per-reel overlay controls — logo / headshot / title / open house.
 * Lives inside Reel Composer and Compile.  Shows a live preview against the
 * listing cover photo as the user toggles things on.
 *
 * `value` shape (sent on the reel POST as `overlays`):
 * {
 *   logo:       {enabled, position, size_pct, bg_pill, start, end}
 *   headshot:   {enabled, position, size_pct, shape, start, end}
 *   title:      {enabled, text, color, align, start, end}
 *   open_house: {enabled, style, headline, label, date, time, address, start, end}
 * }
 */
export default function ReelOverlayControls({ listingId, value, onChange }) {
  const v = value || {};
  const set = (key, patch) => onChange({ ...v, [key]: { ...(v[key] || {}), ...patch } });

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const debounceRef = useRef(null);

  // Debounced live preview — avoid hammering the backend while sliders move
  useEffect(() => {
    if (!listingId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const anyOn = ["logo", "headshot", "title", "open_house"].some((k) => (v[k] || {}).enabled);
      if (!anyOn) {
        setPreviewUrl((u) => {
          if (u) URL.revokeObjectURL(u);
          return null;
        });
        return;
      }
      setPreviewing(true);
      try {
        const res = await api.post(
          `/listings/${listingId}/overlay-preview`,
          { overlays: v },
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(res.data);
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch {
        // silent — preview is non-critical
      } finally {
        setPreviewing(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(v), listingId]);

  const positions = useMemo(
    () => [
      { id: "tl", label: "Top-Left", icon: "ArrowUpLeft" },
      { id: "tr", label: "Top-Right", icon: "ArrowUpRight" },
      { id: "bl", label: "Bottom-Left", icon: "ArrowDownLeft" },
      { id: "br", label: "Bottom-Right", icon: "ArrowDownRight" },
    ],
    []
  );

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 space-y-5">
      <div className="flex items-center gap-2">
        <Icons.Layers size={12} className="text-fuchsia-400" />
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          Reel overlays
        </div>
        {previewing && <Icons.Loader2 size={11} className="animate-spin text-zinc-600" />}
      </div>

      {/* Live preview */}
      {previewUrl && (
        <div className="rounded-lg overflow-hidden border border-white/10 bg-black">
          <img
            src={previewUrl}
            alt="Overlay preview"
            className="w-full aspect-video object-cover"
            data-testid="overlay-preview-img"
          />
        </div>
      )}

      {/* LOGO */}
      <OverlaySection
        title="Brokerage logo"
        icon="Building2"
        accent="text-emerald-400"
        enabled={!!v.logo?.enabled}
        onToggle={(b) => set("logo", { enabled: b })}
        testid="ov-logo"
      >
        <PositionPicker
          value={v.logo?.position || "tr"}
          onChange={(p) => set("logo", { position: p })}
          options={positions}
          testid="ov-logo-pos"
        />
        <RangeRow
          label="Size"
          min={6}
          max={28}
          step={1}
          value={v.logo?.size_pct ?? 14}
          onChange={(n) => set("logo", { size_pct: n })}
          format={(n) => `${n}%`}
          testid="ov-logo-size"
        />
        <Toggle
          label="White pill background (helps on dark photos)"
          on={!!v.logo?.bg_pill}
          onChange={(b) => set("logo", { bg_pill: b })}
          testid="ov-logo-pill"
        />
      </OverlaySection>

      {/* HEADSHOT */}
      <OverlaySection
        title="Realtor headshot"
        icon="UserCircle2"
        accent="text-sky-400"
        enabled={!!v.headshot?.enabled}
        onToggle={(b) => set("headshot", { enabled: b })}
        testid="ov-headshot"
      >
        <PositionPicker
          value={v.headshot?.position || "br"}
          onChange={(p) => set("headshot", { position: p })}
          options={positions}
          testid="ov-headshot-pos"
        />
        <div>
          <div className="text-[11px] text-zinc-400 mb-1.5">Shape</div>
          <div className="flex gap-1.5">
            {[
              { id: "circle", label: "Circle" },
              { id: "rounded", label: "Rounded square" },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => set("headshot", { shape: s.id })}
                className={`text-xs rounded-lg px-3 py-1.5 border transition-all ${
                  (v.headshot?.shape || "rounded") === s.id
                    ? "bg-white text-black border-white"
                    : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                }`}
                data-testid={`ov-headshot-shape-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <RangeRow
          label="Size"
          min={10}
          max={32}
          step={1}
          value={v.headshot?.size_pct ?? 18}
          onChange={(n) => set("headshot", { size_pct: n })}
          format={(n) => `${n}%`}
          testid="ov-headshot-size"
        />
      </OverlaySection>

      {/* TITLE */}
      <OverlaySection
        title="Title text"
        icon="Type"
        accent="text-amber-400"
        enabled={!!v.title?.enabled}
        onToggle={(b) => set("title", { enabled: b })}
        testid="ov-title"
      >
        <div>
          <div className="text-[11px] text-zinc-400 mb-1.5">
            Text <span className="text-zinc-600">(blank = use listing address)</span>
          </div>
          <input
            value={v.title?.text || ""}
            onChange={(e) => set("title", { text: e.target.value })}
            placeholder="THE EDWARDIAN"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
            data-testid="ov-title-text"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] text-zinc-400 mb-1.5">Color</div>
            <input
              type="color"
              value={v.title?.color || "#FFFFFF"}
              onChange={(e) => set("title", { color: e.target.value })}
              className="w-full h-9 bg-transparent border border-white/10 rounded-lg cursor-pointer"
              data-testid="ov-title-color"
            />
          </div>
          <div>
            <div className="text-[11px] text-zinc-400 mb-1.5">Align</div>
            <div className="flex gap-1">
              {["left", "center", "right"].map((a) => (
                <button
                  key={a}
                  onClick={() => set("title", { align: a })}
                  className={`flex-1 text-xs rounded-lg px-2 py-1.5 border transition-all ${
                    (v.title?.align || "left") === a
                      ? "bg-white text-black border-white"
                      : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                  }`}
                  data-testid={`ov-title-align-${a}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </div>
        <TimeRangeRow
          start={v.title?.start ?? 0}
          end={v.title?.end ?? 3}
          onChange={(s, e) => set("title", { start: s, end: e })}
          testid="ov-title-range"
        />
      </OverlaySection>

      {/* OPEN HOUSE */}
      <OverlaySection
        title="Open house"
        icon="CalendarDays"
        accent="text-fuchsia-400"
        enabled={!!v.open_house?.enabled}
        onToggle={(b) => set("open_house", { enabled: b })}
        testid="ov-oh"
      >
        <div>
          <div className="text-[11px] text-zinc-400 mb-1.5">Style</div>
          <div className="flex gap-1.5">
            {[
              { id: "banner", label: "Bottom banner" },
              { id: "pill", label: "Corner pill" },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => set("open_house", { style: s.id })}
                className={`text-xs rounded-lg px-3 py-1.5 border transition-all ${
                  (v.open_house?.style || "banner") === s.id
                    ? "bg-white text-black border-white"
                    : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                }`}
                data-testid={`ov-oh-style-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        {(v.open_house?.style || "banner") === "banner" ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <input
                value={v.open_house?.date || ""}
                onChange={(e) => set("open_house", { date: e.target.value })}
                placeholder="08.10.2025"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
                data-testid="ov-oh-date"
              />
            </Field>
            <Field label="Time">
              <input
                value={v.open_house?.time || ""}
                onChange={(e) => set("open_house", { time: e.target.value })}
                placeholder="2PM"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
                data-testid="ov-oh-time"
              />
            </Field>
            <div className="col-span-2">
              <Field label="Address override (blank = listing address)">
                <input
                  value={v.open_house?.address || ""}
                  onChange={(e) => set("open_house", { address: e.target.value })}
                  placeholder="125 Saddlefield Crescent NE"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
                  data-testid="ov-oh-addr"
                />
              </Field>
            </div>
          </div>
        ) : (
          <Field label="Pill label">
            <input
              value={v.open_house?.label || ""}
              onChange={(e) => set("open_house", { label: e.target.value })}
              placeholder="OPEN SAT"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
              data-testid="ov-oh-label"
            />
          </Field>
        )}
        <TimeRangeRow
          start={v.open_house?.start ?? 0}
          end={v.open_house?.end ?? 0}
          onChange={(s, e) => set("open_house", { start: s, end: e })}
          testid="ov-oh-range"
        />
      </OverlaySection>
    </div>
  );
}

function OverlaySection({ title, icon, accent, enabled, onToggle, children, testid }) {
  const I = Icons[icon] || Icons.Square;
  return (
    <div className="border border-white/5 rounded-xl">
      <button
        onClick={() => onToggle(!enabled)}
        className="w-full flex items-center gap-3 p-3 hover:bg-white/[0.02] transition-all"
        data-testid={`${testid}-toggle`}
      >
        <I size={14} className={accent} />
        <div className="flex-1 text-left text-[12px] tracking-[0.1em] uppercase text-white">
          {title}
        </div>
        <div
          className={`relative w-9 h-5 rounded-full transition-all ${
            enabled ? "bg-white" : "bg-white/10"
          }`}
        >
          <div
            className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
              enabled ? "translate-x-4 bg-black" : "translate-x-0.5 bg-white/60"
            }`}
          />
        </div>
      </button>
      {enabled && (
        <div className="p-4 pt-0 space-y-3 border-t border-white/5">{children}</div>
      )}
    </div>
  );
}

function PositionPicker({ value, onChange, options, testid }) {
  return (
    <div>
      <div className="text-[11px] text-zinc-400 mb-1.5">Position</div>
      <div className="grid grid-cols-4 gap-1.5">
        {options.map((o) => {
          const I = Icons[o.icon] || Icons.Square;
          const on = value === o.id;
          return (
            <button
              key={o.id}
              onClick={() => onChange(o.id)}
              className={`flex flex-col items-center gap-1 rounded-lg px-2 py-2 border text-[10px] transition-all ${
                on
                  ? "bg-white text-black border-white"
                  : "bg-black/40 border-white/10 hover:border-white/30 text-white"
              }`}
              data-testid={`${testid}-${o.id}`}
            >
              <I size={14} />
              <span className="leading-tight">{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RangeRow({ label, min, max, step, value, onChange, format, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] text-zinc-400">{label}</div>
        <div className="text-[11px] font-mono text-zinc-300">
          {format ? format(value) : value}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-white"
        data-testid={testid}
      />
    </div>
  );
}

function TimeRangeRow({ start, end, onChange, testid }) {
  // end===0 means "until end of reel"
  const rangeMode = end > 0 ? "ranged" : "always";
  return (
    <div>
      <div className="text-[11px] text-zinc-400 mb-1.5">When to show</div>
      <div className="flex gap-1.5 mb-2">
        <button
          onClick={() => onChange(0, 0)}
          className={`flex-1 text-xs rounded-lg px-3 py-1.5 border transition-all ${
            rangeMode === "always"
              ? "bg-white text-black border-white"
              : "bg-black/40 border-white/10 hover:border-white/30 text-white"
          }`}
          data-testid={`${testid}-always`}
        >
          Whole reel
        </button>
        <button
          onClick={() => onChange(start || 0, end > 0 ? end : 5)}
          className={`flex-1 text-xs rounded-lg px-3 py-1.5 border transition-all ${
            rangeMode === "ranged"
              ? "bg-white text-black border-white"
              : "bg-black/40 border-white/10 hover:border-white/30 text-white"
          }`}
          data-testid={`${testid}-ranged`}
        >
          Time range
        </button>
      </div>
      {rangeMode === "ranged" && (
        <div className="grid grid-cols-2 gap-2">
          <NumInput
            label="Start (s)"
            value={start}
            onChange={(n) => onChange(n, end)}
            testid={`${testid}-start`}
          />
          <NumInput
            label="End (s)"
            value={end}
            onChange={(n) => onChange(start, n)}
            testid={`${testid}-end`}
          />
        </div>
      )}
    </div>
  );
}

function NumInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] text-zinc-500 mb-1">{label}</div>
      <input
        type="number"
        min="0"
        step="0.5"
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-white/30"
        data-testid={testid}
      />
    </label>
  );
}

function Toggle({ label, on, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded bg-white/5 border border-white/20 checked:bg-white accent-white"
        data-testid={testid}
      />
      <span className="text-[11px] text-zinc-400">{label}</span>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] text-zinc-400 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

// keep `mediaUrl` reachable for any future inline image preview
export const _unused = mediaUrl;
